import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import type { Meeting, Recording } from '@syndiqia/governance/contracts';
import { errorMessage, useApi, useAuthHeaders } from '../../api';
import { Alert } from '../../components/ui';
import { useGovernanceMutation } from '../../governance-queries';
import { useI18n } from '../../i18n';
import { useSyndicate } from '../../session';

const CHUNK_MS = 30_000;
const MIME_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];

function useRecordings(meetingId: string) {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({
    queryKey: [s.tenantId, 'governance', 'recordings', meetingId],
    queryFn: () => api<Recording[]>(`${s.api}/governance/meetings/${meetingId}/recordings`),
    // While a transcription runs, check again.
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === 'uploaded' || r.status === 'transcribing') ? 15_000 : false),
  });
}

/** Recording of the meeting (step 3): record here, import a file or a transcript, follow the transcription. */
export function RecordingPanel({ meeting, editable }: { meeting: Meeting; editable: boolean }) {
  const { t } = useI18n();
  const g = t.governance;
  const recordings = useRecordings(meeting.id);
  const visible = (recordings.data ?? []).filter((r) => r.status !== 'purged');
  if (!editable && visible.length === 0) return null;
  return (
    <section className="panel recording-panel">
      <h2>🎙 {g.recording}</h2>
      <p className="muted small">{g.recordingHint}</p>
      {editable && <Recorder meeting={meeting} />}
      {visible.map((r) => (
        <RecordingCard key={r.id} meeting={meeting} recording={r} editable={editable} />
      ))}
      {editable && <ImportRecording meetingId={meeting.id} />}
    </section>
  );
}

function Recorder({ meeting }: { meeting: Meeting }) {
  const { t, locale } = useI18n();
  const g = t.governance;
  const s = useSyndicate();
  const api = useApi();
  const authHeaders = useAuthHeaders();
  const queryClient = useQueryClient();
  const [state, setState] = useState<'idle' | 'recording' | 'paused' | 'finishing'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [problem, setProblem] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const ref = useRef<{ recorder: MediaRecorder; stream: MediaStream; id: string; seq: number; queue: Promise<void>; accumulated: number; since: number | null } | null>(null);

  const elapsedMs = () => {
    const r = ref.current;
    return r ? r.accumulated + (r.since ? Date.now() - r.since : 0) : 0;
  };
  useEffect(() => {
    if (state !== 'recording' && state !== 'paused') return;
    const timer = setInterval(() => setElapsed(elapsedMs()), 1000);
    const warn = (e: BeforeUnloadEvent) => (e.preventDefault(), (e.returnValue = g.leaveWarning));
    window.addEventListener('beforeunload', warn);
    return () => (clearInterval(timer), window.removeEventListener('beforeunload', warn));
  }, [state, g.leaveWarning]);

  const upload = async (id: string, seq: number, blob: Blob) => {
    for (let attempt = 0; ; attempt++) {
      try {
        const res = await fetch(`${s.api}/governance/recordings/${id}/chunks/${seq}`, { method: 'PUT', headers: { ...authHeaders(), 'Content-Type': 'application/octet-stream' }, body: blob });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setRetrying(false);
        setUploadedBytes((b) => b + blob.size);
        return;
      } catch {
        if (attempt >= 8) throw new Error('upload');
        setRetrying(true);
        await new Promise((r) => setTimeout(r, Math.min(30_000, 1000 * 2 ** attempt)));
      }
    }
  };
  const mark = (index: number) => {
    const item = meeting.items[index];
    const r = ref.current;
    if (item && r) void api(`${s.api}/governance/recordings/${r.id}/markers`, { method: 'POST', body: { itemId: item.id, offsetMs: Math.round(elapsedMs()) } }).catch(() => undefined);
  };

  const start = async () => {
    setProblem(null);
    const mimeType = typeof MediaRecorder === 'undefined' ? undefined : MIME_TYPES.find((m) => MediaRecorder.isTypeSupported(m));
    if (!mimeType || !navigator.mediaDevices?.getUserMedia) return setProblem(g.notSupported);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    } catch {
      return setProblem(g.micDenied);
    }
    try {
      const rec = await api<{ id: string }>(`${s.api}/governance/meetings/${meeting.id}/recordings`, { method: 'POST', body: { mimeType, language: locale === 'fr' ? 'fr-CA' : 'en-US' } });
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32_000 });
      const current = { recorder, stream, id: rec.id, seq: 0, queue: Promise.resolve(), accumulated: 0, since: Date.now() as number | null };
      ref.current = current;
      recorder.ondataavailable = (e) => {
        if (e.data.size === 0) return;
        const seq = current.seq++;
        current.queue = current.queue.then(() => upload(current.id, seq, e.data)).catch(() => setProblem(t.common.error));
      };
      recorder.start(CHUNK_MS);
      setUploadedBytes(0);
      setItemIndex(0);
      setState('recording');
      mark(0); // the first agenda item starts now
      void queryClient.invalidateQueries({ queryKey: [s.tenantId, 'governance', 'recordings', meeting.id] });
    } catch (e) {
      stream.getTracks().forEach((track) => track.stop());
      setProblem(errorMessage(e, t));
    }
  };
  const pause = () => {
    const r = ref.current!;
    r.recorder.pause();
    r.accumulated += r.since ? Date.now() - r.since : 0;
    r.since = null;
    setState('paused');
  };
  const resume = () => {
    const r = ref.current!;
    r.recorder.resume();
    r.since = Date.now();
    setState('recording');
  };
  const next = () => {
    const index = Math.min(itemIndex + 1, meeting.items.length - 1);
    setItemIndex(index);
    mark(index);
  };
  const finish = async () => {
    const r = ref.current!;
    setState('finishing');
    const durationMs = Math.round(elapsedMs());
    const stopped = new Promise<void>((resolve) => (r.recorder.onstop = () => resolve()));
    r.recorder.stop();
    await stopped;
    await r.queue; // last chunks uploaded
    r.stream.getTracks().forEach((track) => track.stop());
    try {
      await api(`${s.api}/governance/recordings/${r.id}/finish`, { method: 'POST', body: { durationMs } });
    } catch (e) {
      setProblem(errorMessage(e, t));
    }
    ref.current = null;
    setState('idle');
    void queryClient.invalidateQueries({ queryKey: [s.tenantId, 'governance', 'recordings', meeting.id] });
  };

  const clock = `${Math.floor(elapsed / 3_600_000)}:${String(Math.floor(elapsed / 60_000) % 60).padStart(2, '0')}:${String(Math.floor(elapsed / 1000) % 60).padStart(2, '0')}`;
  return (
    <div className="recorder">
      {state === 'idle' ? (
        <>
          <p className="consent small">{g.consent}</p>
          <button type="button" className="button button-primary" onClick={() => void start()}>
            ● {g.startRecording}
          </button>
        </>
      ) : (
        <>
          <div className="recorder-status">
            <span className={`rec-dot${state === 'recording' ? ' rec-live' : ''}`} />
            <strong className="rec-clock">{clock}</strong>
            <span>{state === 'paused' ? g.pausedNow : g.recordingNow(`${itemIndex + 1}. ${meeting.items[itemIndex]?.title ?? ''}`)}</span>
            <span className="muted small">{retrying ? g.uploadRetry : g.uploaded((uploadedBytes / 1_048_576).toFixed(1))}</span>
          </div>
          <div className="form-actions">
            <button type="button" className="button button-primary" disabled={state !== 'recording' || itemIndex >= meeting.items.length - 1} onClick={next}>
              {g.nextItem} →
            </button>
            {state === 'recording' && (
              <button type="button" className="button" onClick={pause}>
                ❚❚ {g.pause}
              </button>
            )}
            {state === 'paused' && (
              <button type="button" className="button" onClick={resume}>
                ▶ {g.resume}
              </button>
            )}
            <button type="button" className="button" disabled={state === 'finishing'} onClick={() => void finish()}>
              ■ {g.finishRecording}
            </button>
          </div>
        </>
      )}
      {problem && <Alert>{problem}</Alert>}
    </div>
  );
}

function RecordingCard({ meeting, recording: r, editable }: { meeting: Meeting; recording: Recording; editable: boolean }) {
  const { t, date } = useI18n();
  const g = t.governance;
  const [open, setOpen] = useState(false);
  const speakers = [...new Set(r.segments.map((x) => x.speaker))].filter(Boolean);
  const [names, setNames] = useState<Record<string, string>>(r.speakerNames);
  const save = useGovernanceMutation<Record<string, string>, void>((api, base, speakerNames) => api(`${base}/governance/recordings/${r.id}`, { method: 'PATCH', body: { speakerNames } }));
  const retry = useGovernanceMutation<void, void>((api, base) => api(`${base}/governance/recordings/${r.id}/retry`, { method: 'POST' }));
  const label = (speaker: string) => names[speaker] || (/^spk_\d+$/.test(speaker) ? g.speakerLabel(Number(speaker.slice(4)) + 1) : speaker || '—');
  const itemAt = (ms: number) => {
    const marker = [...r.markers].sort((a, b) => a.offsetMs - b.offsetMs).filter((m) => m.offsetMs <= ms).at(-1);
    return marker ? meeting.items.find((i) => i.id === marker.itemId) : undefined;
  };

  return (
    <div className="recording-card">
      <div className="recording-head">
        <span className={`badge badge-rec-${r.status}`}>{g.recordingStatuses[r.status]}</span>
        <span className="small">
          {g.recordingSources[r.source]} · {date(r.startedAt.slice(0, 10))}
          {r.durationMs ? ` · ${g.duration(Math.max(1, Math.round(r.durationMs / 60_000)))}` : ''}
        </span>
        {r.status === 'failed' && editable && r.source !== 'transcript' && (
          <button type="button" className="link-button small" disabled={retry.isPending} onClick={() => retry.mutate()}>
            {g.retry}
          </button>
        )}
      </div>
      {r.error && <p className="small field-error">{r.error}</p>}
      {r.status === 'transcribed' && (
        <>
          <p className="muted small">{g.transcriptUsed}</p>
          {speakers.length > 0 && editable && (
            <details>
              <summary>{g.speakers}</summary>
              <p className="muted small">{g.speakersHint}</p>
              <div className="speaker-names">
                {speakers.map((sp) => (
                  <label key={sp} className="inline-field">
                    <span>{/^spk_\d+$/.test(sp) ? g.speakerLabel(Number(sp.slice(4)) + 1) : sp}</span>
                    <input value={names[sp] ?? ''} onChange={(e) => setNames((n) => ({ ...n, [sp]: e.target.value }))} onBlur={() => save.mutate(names)} />
                  </label>
                ))}
              </div>
            </details>
          )}
          <button type="button" className="link-button small" onClick={() => setOpen(!open)}>
            {g.transcript} {open ? '▴' : '▾'}
          </button>
          {open && (
            <div className="transcript">
              {r.segments.map((seg, i) => {
                const item = itemAt(seg.startMs);
                const prev = i > 0 ? itemAt(r.segments[i - 1]!.startMs) : undefined;
                return (
                  <div key={seg.seq}>
                    {item && item !== prev && <p className="transcript-item">{`${item.position}. ${item.title}`}</p>}
                    <p className="small">
                      <strong>{label(seg.speaker)}</strong> {seg.text}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ImportRecording({ meetingId }: { meetingId: string }) {
  const { t } = useI18n();
  const g = t.governance;
  const authHeaders = useAuthHeaders();
  const upload = useGovernanceMutation<File, unknown>(async (_api, base, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${base}/governance/meetings/${meetingId}/recordings/import`, { method: 'POST', headers: authHeaders(), body: form });
    const body = await res.json().catch(() => null);
    if (!res.ok) throw Object.assign(new Error(body?.message ?? 'Upload failed'), { status: res.status, body });
    return body;
  });
  return (
    <details className="import-recording">
      <summary>{g.importRecording}</summary>
      <p className="muted small">{g.importHint}</p>
      <input type="file" accept="audio/*,video/mp4,.vtt,.txt,text/vtt,text/plain" disabled={upload.isPending} onChange={(e) => e.target.files?.[0] && upload.mutate(e.target.files[0])} />
      {upload.isPending && <p className="muted small">{g.importing}</p>}
      {upload.isError && <Alert>{(upload.error as Error).message}</Alert>}
    </details>
  );
}
